import type { AttributionPayload } from './attribution';
import type { StoredConsent } from './consent';
import { mapCanonicalEvent, rememberEvent, sanitizeEventParams, type CanonicalEvent, type FiredEventStore } from './events';
import { adsSendTo, linkedInConversionId, type TagConfig } from './tags';

export interface BrowserTarget {
  gtag?: (...args: unknown[]) => void;
  fbq?: (...args: unknown[]) => void;
  lintrk?: (...args: unknown[]) => void;
}

export interface RehubInstalledTags {
  ga4?: string;
  googleAds?: string;
  meta?: string;
  linkedin?: string;
}

/** Sends a canonical event to the platforms the visitor has allowed. */
export function dispatchBrowserEvent(input: {
  event: CanonicalEvent;
  eventId: string;
  params?: Record<string, unknown>;
  config: TagConfig;
  consent: StoredConsent;
  storage: FiredEventStore;
  target: BrowserTarget;
}): boolean {
  try {
    if (!input.target.gtag && !input.target.fbq && !input.target.lintrk) return false;
    if (!rememberEvent(input.storage, input.eventId)) return false;
    const params = sanitizeEventParams({
      ...(input.params || {}),
      event_id: input.eventId,
      ...(input.event === 'purchase' ? { transaction_id: input.eventId } : {}),
    });
    const gaParams = input.config.debugMode ? { ...params, debug_mode: true } : params;
    const mapped = mapCanonicalEvent(input.event);
    const metaOptions: Record<string, unknown> = { eventID: input.eventId };
    if (input.config.metaTestEventCode) metaOptions.test_event_code = input.config.metaTestEventCode;

    const call = (fn: (() => void) | undefined) => {
      try {
        fn?.();
      } catch {
        /* one platform failing must not block the others or the page */
      }
    };

    if (input.consent.analytics && typeof input.target.gtag === 'function') {
      const gtag = input.target.gtag;
      call(() => gtag('event', mapped.ga4Name, gaParams));
    }

    if (input.consent.advertising && typeof input.target.fbq === 'function') {
      const fbq = input.target.fbq;
      const metaParams = { ...params };
      call(() => {
        if (mapped.metaCustom) fbq('trackCustom', mapped.metaName, metaParams, metaOptions);
        else fbq('track', mapped.metaName, metaParams, metaOptions);
      });
    }

    // debugMode is local DebugView only. It must not hit the production Ads actions.
    if (input.consent.advertising && mapped.adsSlot && typeof input.target.gtag === 'function' && !input.config.debugMode) {
      const sendTo = adsSendTo(input.config, mapped.adsSlot);
      const gtag = input.target.gtag;
      if (sendTo) {
        const conversion: Record<string, unknown> = { send_to: sendTo, transaction_id: input.eventId };
        if (typeof params.value === 'number') conversion.value = params.value;
        if (typeof params.currency === 'string') conversion.currency = params.currency;
        call(() => gtag('event', 'conversion', conversion));
      }
    }

    if (input.consent.advertising && mapped.linkedinSlot && typeof input.target.lintrk === 'function') {
      const conversionId = linkedInConversionId(input.config, mapped.linkedinSlot);
      const lintrk = input.target.lintrk;
      if (conversionId) call(() => lintrk('track', { conversion_id: conversionId }));
    }

    return true;
  } catch {
    return false;
  }
}

interface TagHost {
  gtag?: unknown;
  fbq?: unknown;
  lintrk?: unknown;
  __rehubTracking?: RehubInstalledTags;
}

function asTracker(value: unknown): ((...args: unknown[]) => void) | undefined {
  return typeof value === 'function' ? (value as (...args: unknown[]) => void) : undefined;
}

function hostOf(win: object): TagHost {
  return win as TagHost;
}

export function browserTargetFromWindow(win: object): BrowserTarget {
  const host = hostOf(win);
  return { gtag: asTracker(host.gtag), fbq: asTracker(host.fbq), lintrk: asTracker(host.lintrk) };
}

/** Uses only the tags this package installed, so a legacy snippet is not called as well. */
export function rehubBrowserTarget(win: object, config: TagConfig): BrowserTarget {
  const host = hostOf(win);
  const mark = host.__rehubTracking;
  const ownsGtag = Boolean(
    (config.ga4MeasurementId && mark?.ga4 === config.ga4MeasurementId)
    || (config.googleAdsId && mark?.googleAds === config.googleAdsId),
  );
  return {
    gtag: ownsGtag ? asTracker(host.gtag) : undefined,
    fbq: mark?.meta && mark.meta === config.metaPixelId ? asTracker(host.fbq) : undefined,
    lintrk: mark?.linkedin ? asTracker(host.lintrk) : undefined,
  };
}

export type { AttributionPayload };
